import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { style: {"font-size":"1.1em"} }
const _hoisted_2 = { style: {"font-size":"1.3em"} }

import { IonSkeletonText, IonToast, IonAlert, IonIcon, IonButton, IonItem, useIonRouter, IonLoading } from '@ionic/vue';
import { onMounted, onUnmounted, ref } from 'vue';
import { Storage } from '@ionic/storage';
import { cloudUpload, camera, settings, fileTrayFull, information } from 'ionicons/icons';
import { Base64RecognizeRequest, Configuration, MobileApi, ProcessResult } from 'plate-vision-demo-openapi-client';
import { usePhotoGallery } from '@/components/usePhotoGallery';
import { Photo } from '@capacitor/camera';
import { AxiosResponse } from 'axios';
import { Network } from '@capacitor/network';
import { useI18n } from 'vue-i18n';

interface FinalReport {
    response: ProcessResult;
    date: string;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'HomePage',
  emits: ["value-changed"],
  setup(__props, { emit }: { emit: ({
    (e: 'value-changed', id: string): void
}), expose: any, slots: any, attrs: any }) {

const { t } = useI18n({
    inheritLocale: true,
    useScope: 'global',
});



const sendValueToParent = (id: string) => {
    emit('value-changed', id);
};

const { takePhoto, uploadPhoto, resizePhoto } = usePhotoGallery();

const router = useIonRouter();

async function uploadPhotoAndSend() {
    await refreshLocationAndDetectType();
    if (await getInternetStatus()) {
        uploadPhoto()
            .then((photo: Photo) => {
                sendPhoto(photo);
            })
            .catch((error) => {
                console.log(error);
            });
    } else {
        showErrorConnection.value = true;
    }
}

async function takePhotoAndSend() {
    await refreshLocationAndDetectType();
    if (await getInternetStatus()) {
        takePhoto()
            .then((photo: Photo) => {
                sendPhoto(photo);
            })
            .catch((error) => {
                console.log(error);
            });
    } else {
        showErrorConnection.value = true;
    }
}

const resizedImage = ref(false);

async function sendPhoto(photo: Photo) {
    if (mobileApi) {
        if (photo.dataUrl) {
            isLoading.value = true;
            if (photo.dataUrl.length > 4000000) {
                resizedImage.value = true;
                photoSettings.value.imageBase64 = await resizePhoto(photo.dataUrl, 1920, 1080);
            } else {
                photoSettings.value.imageBase64 = photo.dataUrl.slice(photo.dataUrl.indexOf(',') + 1);
            }
            photoSettings.value.imageMimeType = 'image/' + photo.format;
            mobileApi.recognizeByBase64(photoSettings.value).then((response: AxiosResponse<ProcessResult>) => {
                credits.value--;
                finalReport.response = response.data;
                finalReport.date = new Date().toLocaleString();
                storage.get('reports').then((reportsArray) => {
                    if (reportsArray) {
                        reportHistory = reportsArray;
                    }
                    reportHistory.splice(0, 0, finalReport);
                    storage.set('reports', reportHistory).then(() => {
                        if (window.innerWidth >= 750) {
                            sendValueToParent(finalReport.response.responseUuid);
                            isLoading.value = false;
                        }
                        router.push({ name: 'report', params: { index: 0 } });
                    });
                });
            });
        } else {
            showErrorConnection.value = true;
        }
    } else {
        console.error('mobileApi not defined');
        showErrorConnection.value = true;
    }
}

let reportHistory: FinalReport[] = [];
const storage = new Storage();
const credits = ref<number>(20);
let mobileApi: MobileApi;

const finalReport: FinalReport = {
    response: {
        processedImage: '',
        results: [],
        toDetectMakeAndModel: false,
        sendCountry: false,
        imgType: '',
        imgQualityFloat: 0,
        imgQuality: 0,
        imgHeight: 0,
        imgWidth: 0,
        processedImageMimeType: '',
        name: '',
        responseUuid: '',
    },
    date: '',
};

const creditsLoaded = ref<boolean>(false);

function refreshLocationAndDetectType() {
    storage.get('plateLocation').then((plateLocation: string) => {
        photoSettings.value.plateLocation = plateLocation;
    });
    storage.get('detect').then((detect: boolean) => {
        photoSettings.value.toDetectMakeAndModel = detect;
    });
}

// before it renders the page, it will get the stored values for the plate location, detection type, usage left and token
onMounted(() => {
    storage.create().then(() => {
        // get the stored values for the plate location and detection type
        refreshLocationAndDetectType();
        // get the token from the storage and create mobileApi
        storage
            .get('token')
            .then((token: string) => {
                mobileApi = new MobileApi(
                    new Configuration({ accessToken: token, basePath: process.env.VUE_APP_PATH })
                );
            })
            .then(() => {
                // get the credits left
                mobileApi
                    .getCredits()
                    .then((response: AxiosResponse) => {
                        credits.value = response.data.creditsLeft;
                        creditsLoaded.value = true;
                    })
                    .catch((error) => {
                        console.log(error);
                    });
            });
    });
});

onUnmounted(() => {
    isLoading.value = false;
});

const isLoading = ref(false);
const showErrorConnection = ref(false);

async function getInternetStatus(): Promise<boolean> {
    const network = await Network.getStatus();
    return network.connected;
}

const photoSettings = ref<Base64RecognizeRequest>({
    plateLocation: 'eu',
    toDetectMakeAndModel: false,
    imageBase64: '',
    imageMimeType: '',
});


return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(IonAlert), {
      "is-open": showErrorConnection.value,
      header: _unref(t)('homepage.alertHeader'),
      subHeader: _unref(t)('homepage.alertSubHeader'),
      message: _unref(t)('homepage.alertMessage'),
      buttons: ['OK'],
      onDidDismiss: _cache[0] || (_cache[0] = ($event: any) => (showErrorConnection.value = false))
    }, null, 8, ["is-open", "header", "subHeader", "message"]),
    (credits.value == 0)
      ? (_openBlock(), _createBlock(_unref(IonItem), {
          key: 0,
          class: "bradius12 margin-1-rem-tb",
          color: "warning"
        }, {
          default: _withCtx(() => [
            _createElementVNode("p", null, _toDisplayString(_unref(t)('homepage.noMoreCredits')), 1)
          ]),
          _: 1
        }))
      : (_openBlock(), _createBlock(_unref(IonItem), {
          key: 1,
          class: "bradius12 margin-1-rem-tb"
        }, {
          default: _withCtx(() => [
            _createElementVNode("span", _hoisted_1, _toDisplayString(_unref(t)('homepage.dailyUsage')), 1),
            (creditsLoaded.value)
              ? (_openBlock(), _createBlock(_unref(IonButton), {
                  key: 0,
                  class: "credit-button",
                  fill: "outline"
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("span", _hoisted_2, _toDisplayString(credits.value), 1)
                  ]),
                  _: 1
                }))
              : (_openBlock(), _createBlock(_unref(IonSkeletonText), {
                  key: 1,
                  animated: true,
                  class: "credit-button",
                  style: {"border":"#2c81e6 2px solid","border-radius":"4px","margin":"0 2px"}
                }))
          ]),
          _: 1
        })),
    _createVNode(_component_router_link, { to: { name: 'startGuide' } }, {
      default: _withCtx(() => [
        _createVNode(_unref(IonButton), {
          shape: "round",
          size: "large",
          expand: "block",
          class: "margin-1-rem-tb"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(IonIcon), {
              slot: "start",
              icon: _unref(information)
            }, null, 8, ["icon"]),
            _createElementVNode("span", null, _toDisplayString(_unref(t)('homepage.startGuide')), 1)
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createVNode(_unref(IonButton), {
      disabled: credits.value == 0 ? true : false,
      shape: "round",
      size: "large",
      expand: "block",
      class: "margin-1-rem-tb",
      onClick: _cache[1] || (_cache[1] = ($event: any) => (takePhotoAndSend()))
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(IonIcon), {
          slot: "start",
          icon: _unref(camera)
        }, null, 8, ["icon"]),
        _createElementVNode("span", null, _toDisplayString(_unref(t)('homepage.takePhoto')), 1)
      ]),
      _: 1
    }, 8, ["disabled"]),
    _createVNode(_unref(IonButton), {
      disabled: credits.value == 0 ? true : false,
      shape: "round",
      size: "large",
      expand: "block",
      class: "margin-1-rem-tb",
      onClick: _cache[2] || (_cache[2] = ($event: any) => (uploadPhotoAndSend()))
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(IonIcon), {
          slot: "start",
          icon: _unref(cloudUpload)
        }, null, 8, ["icon"]),
        _createElementVNode("span", null, _toDisplayString(_unref(t)('homepage.uploadPhoto')), 1)
      ]),
      _: 1
    }, 8, ["disabled"]),
    _createVNode(_component_router_link, { to: { name: 'reports' } }, {
      default: _withCtx(() => [
        _createVNode(_unref(IonButton), {
          shape: "round",
          size: "large",
          expand: "block",
          class: "margin-1-rem-tb"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(IonIcon), {
              slot: "start",
              icon: _unref(fileTrayFull)
            }, null, 8, ["icon"]),
            _createElementVNode("span", null, _toDisplayString(_unref(t)('homepage.reports')), 1)
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createVNode(_component_router_link, { to: { name: 'preferences' } }, {
      default: _withCtx(() => [
        _createVNode(_unref(IonButton), {
          shape: "round",
          size: "large",
          expand: "block",
          class: "margin-1-rem-tb"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(IonIcon), {
              slot: "start",
              icon: _unref(settings)
            }, null, 8, ["icon"]),
            _createElementVNode("span", null, _toDisplayString(_unref(t)('homepage.preferences')), 1)
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createVNode(_unref(IonLoading), {
      "is-open": isLoading.value,
      message: _unref(t)('homepage.processingReport')
    }, null, 8, ["is-open", "message"]),
    _createVNode(_unref(IonToast), {
      "is-open": resizedImage.value,
      message: _unref(t)('homepage.resizeImage'),
      duration: 3000
    }, null, 8, ["is-open", "message"])
  ], 64))
}
}

})